// src/pages/home.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next'; // 1. ייבוא ה-hook
import { Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import NewsletterSignup from '../components/blog/newsletterSignup';
import FeaturedPost from '../components/blog/featuredPost';
import PostCard from '../components/blog/postCard';
import CategoryFilter from '../components/blog/categoryFilter';
import SearchDialog from '../components/blog/searchDialog';
import { BlogPost } from '../entities/BlogPost';

export default function HomePage() {
  const { t, i18n } = useTranslation(); // 2. הפעלת ה-hook
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);

  // 3. עדכון ה-useEffect כך שיפעל כשהשפה משתנה
  useEffect(() => {
    // קוראים לפונקציה עם השפה הנוכחית מה-i18n
    loadPosts(i18n.language);

    const urlParams = new URLSearchParams(window.location.search);
    const categoryParam = urlParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [i18n.language]); // הוספנו את השפה כתלות!

  const loadPosts = async (lang) => {
    setIsLoading(true);
    try {
      const fetchedPosts = await BlogPost.list(lang);
      setPosts(fetchedPosts);
    } catch (error) {
      console.error("Failed to load posts:", error);
      setPosts([]); // איפוס המערך במקרה של שגיאה
    } finally {
      setIsLoading(false);
    }
  };

  const featuredPosts = posts.filter(post => post.isFeatured);
  const filteredPosts = posts.filter(post => (selectedCategory === 'all' ? true : post.category === selectedCategory));

  return (
      <div>
        {/* Hero Section - 4. תרגום כל הטקסטים */}
        <section className="hero-pattern py-20 px-4 mb-12">
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex-1">
                <motion.h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                  {t('hero.title')}
                </motion.h1>
                <motion.p className="text-xl md:text-2xl text-[#b8c1ec] mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                  {t('hero.subtitle')}
                </motion.p>
                <motion.div className="flex gap-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
                  <Button onClick={() => setShowSearch(true)} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                    <Search className="w-4 h-4 ml-2" />
                    {t('hero.search_button')}
                  </Button>
                  <Link to={`/${i18n.language}/categories`}>
                    <Button className="bg-[#eebbc3] text-[#232946] hover:bg-[#e5a9b3]">
                      {t('hero.all_categories_button')}
                    </Button>
                  </Link>
                </motion.div>
              </motion.div>
              <motion.div className="flex-1 relative" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }}>
                <div className="relative w-full aspect-square flex items-center justify-center">
                  <img src="/myLogo.png" alt={t('hero.title')} className="w-4/5 h-4/5 object-contain nod-animation" />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Main Content */}
            <div className="flex-1">
              {/* סינון קטגוריות */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-[#232946] mb-4">
                  {selectedCategory !== 'all'
                      ? t('pages.home.posts_in_category', { categoryName: t(`categories.${selectedCategory}`) })
                      : t('pages.home.all_posts')}
                </h2>
                <CategoryFilter selectedCategory={selectedCategory} onCategoryChange={setSelectedCategory} />
              </div>

              {/* רשת פוסטים מסוננת */}
              {isLoading ? (
                  <div className="text-center py-12">
                    <p className="text-[#8890b3] text-lg">{t('status.loading_posts')}</p>
                  </div>
              ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {filteredPosts.map((post) => (
                          <PostCard key={post.id} post={post} />
                      ))}
                    </div>
                    {selectedCategory !== 'all' && filteredPosts.length === 0 && (
                        <div className="text-center py-12">
                          <p className="text-[#8890b3] text-lg">{t('pages.home.no_posts_in_category')}</p>
                        </div>
                    )}
                  </>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:w-80 flex flex-col gap-8">
              <div className="sticky top-24 flex flex-col gap-8">
                <NewsletterSignup compact={false} />
                {featuredPosts.length > 0 && (
                    <div className="bg-[#faf9fa] border border-[#eebbc3] rounded-2xl shadow px-4 py-6">
                      <h3 className="text-lg font-bold text-[#232946] mb-4 border-b pb-2 border-[#eebbc3]/30">
                        {t('pages.home.featured_posts')}
                      </h3>
                      <div className="space-y-4">
                        {featuredPosts.map(post => ( <FeaturedPost key={post.id} post={post} small /> ))}
                      </div>
                    </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <SearchDialog open={showSearch} onOpenChange={setShowSearch} />
      </div>
  );
}